import { Icone } from '../../design/Icone';
import { hojeBrasilia, horaCurta, inteiro, pctTexto, reais, tomDaVariacao } from '../../paineis';
import type { Indicador, PainelDoDia } from '../../paineis';
import {
  comMilhar,
  dadosDo,
  fonteDo,
  horaDoHos,
  primeiraParte,
  useAgora,
  usePainelDoDia,
} from './home-comum';
import './tv.css';

/**
 * TELA 12 C — MODO TV. Painel de parede: sem sidebar, sem hover, sem tooltip, sem tabela.
 *
 * Fundo grafite porque a tela fica ligada o dia inteiro num monitor a três metros do depósito.
 * Número grande, poucos blocos, um indicador discreto de atualização. Relê sozinho a cada 60 s;
 * se a leitura falhar, continua mostrando o último dado bom e diz de que horas ele é.
 */

const RELEITURA_MS = 60_000;

export function ModoTV({ permitido }: { parametros: URLSearchParams; permitido: boolean }) {
  // Sem `painel.ver_completo` nem pergunta ao servidor: o aviso é o mesmo que o 403 daria.
  return permitido ? <PainelDaParede /> : <ParedeSemAcesso />;
}

function ParedeSemAcesso() {
  return (
    <main className="tv" aria-label="Modo TV">
      <div className="tv-aviso">
        <strong>Este acesso não abre o painel da parede</strong>
        <span>O Modo TV é do administrador. Entre com uma conta de administração.</span>
      </div>
      <footer className="tv-rodape">
        <a href="#/visao-geral" className="tv-sair">
          <Icone nome="arrow-l" tamanho={13} traco={1.6} />
          Sair do modo TV
        </a>
      </footer>
    </main>
  );
}

function PainelDaParede() {
  const { estado } = usePainelDoDia(undefined, RELEITURA_MS);
  const agora = useAgora(15_000);
  const ultimo = dadosDo(estado);
  const d = ultimo?.dados ?? null;
  const falhou = estado.fase === 'erro';
  const semAcesso = falhou && estado.falha.tipo === 'sem-acesso';
  const relogio = new Date(agora);

  return (
    <main className="tv" aria-label="Modo TV">
      <header className="tv-topo">
        <span className="tv-marca" aria-hidden="true">
          +
        </span>
        <div className="tv-nome">
          <strong>InovaPay</strong>
          <span>REDE INOVA · MURIAÉ</span>
        </div>
        <div className="tv-espaco" />
        <div className="tv-relogio">
          <strong>{horaCurta(relogio.toISOString())}</strong>
          <span>
            {relogio.toLocaleDateString('pt-BR', {
              weekday: 'long',
              day: '2-digit',
              month: 'long',
              timeZone: 'America/Sao_Paulo',
            })}
          </span>
        </div>
        <div className={`tv-estado${falhou ? ' tv-estado-velho' : ''}`} role="status">
          <i aria-hidden="true" />
          {ultimo
            ? falhou
              ? `sem conexão · dados das ${horaCurta(new Date(ultimo.lidoEm).toISOString())}`
              : `atualizado às ${horaCurta(new Date(ultimo.lidoEm).toISOString())}`
            : falhou
              ? 'sem conexão'
              : 'carregando'}
        </div>
      </header>

      {d ? (
        <Quadro d={d} />
      ) : (
        <div className="tv-aviso">
          {semAcesso ? (
            <>
              <strong>Este acesso não abre o painel da parede</strong>
              <span>O Modo TV é do administrador. Entre com uma conta de administração.</span>
            </>
          ) : falhou ? (
            <>
              <strong>Não foi possível carregar o painel</strong>
              <span>Nova tentativa automática em até 1 minuto.</span>
            </>
          ) : (
            <strong>Carregando o painel…</strong>
          )}
        </div>
      )}

      <footer className="tv-rodape">
        <a href="#/visao-geral" className="tv-sair">
          <Icone nome="arrow-l" tamanho={13} traco={1.6} />
          Sair do modo TV
        </a>
        <div className="tv-espaco" />
        {d && <span>{rodape(d)}</span>}
      </footer>
    </main>
  );
}

function rodape(d: PainelDoDia): string {
  const vendas = fonteDo(d.frescor, 'vendas');
  const hos = horaDoHos(d.frescor);
  const diaDaVenda = vendas?.ate
    ? new Date(vendas.ate).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' })
    : null;
  const partes = [
    vendas?.ate && diaDaVenda
      ? `LANÇAMENTOS ATÉ ${horaCurta(vendas.ate)}${diaDaVenda === hojeBrasilia() ? '' : ` DE ${diaDaVenda.slice(8, 10)}/${diaDaVenda.slice(5, 7)}`}`
      : 'NENHUM LANÇAMENTO AINDA',
    hos ? `RELATÓRIO HOS DE ${hos}` : 'SEM RELATÓRIO DO HOS',
  ];
  return partes.join(' · ');
}

type Disponivel = Extract<Indicador, { disponivel: true }>;

function Fora({ i }: { i: Extract<Indicador, { disponivel: false }> }) {
  return (
    <>
      <div className="tv-fora">indisponível</div>
      <div className="tv-motivo">{i.porque}</div>
    </>
  );
}

function Quadro({ d }: { d: PainelDoDia }) {
  const k = d.kpis;
  const giro = d.operacao.find((m) => m.modulo === 'giro');
  const alerta = d.atencao.find((a) => a.severidade === 'critica' || a.severidade === 'atencao');
  const ranking = d.destaques.linhas.slice(0, 4);
  return (
    <div className="tv-corpo">
      <div className="tv-linha1">
        <section className="tv-bloco">
          <h2 className="tv-rotulo">VENDIDO HOJE</h2>
          {k.vendas.disponivel ? (
            <>
              <div className="tv-valor tv-valor-maior">{reais(k.vendas.valor, 0)}</div>
              <div className="tv-var-linha">
                {k.vendas.variacao && <Variacao i={k.vendas} />}
                <span>
                  {[
                    k.vendas.variacao ? `vs ${k.vendas.variacao.base}` : null,
                    k.itens.disponivel ? `${inteiro(k.itens.valor)} itens` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
            </>
          ) : (
            <Fora i={k.vendas} />
          )}
        </section>

        <section className="tv-bloco">
          <h2 className="tv-rotulo">META DA REDE</h2>
          {k.meta.disponivel ? (
            <>
              <div className="tv-valor">{inteiro(k.meta.valor)}%</div>
              <div className="tv-barra">
                <i style={{ width: `${String(Math.max(0, Math.min(100, k.meta.valor)))}%` }} />
              </div>
              <div className="tv-detalhe">{comMilhar(primeiraParte(k.meta.detalhe))}</div>
            </>
          ) : (
            <Fora i={k.meta} />
          )}
        </section>

        <section className="tv-bloco">
          <h2 className="tv-rotulo">GIRO DE PRATELEIRA</h2>
          {k.giro.disponivel ? (
            <>
              <div className="tv-valor">{inteiro(k.giro.valor)}%</div>
              {giro && giro.selos.length > 0 && (
                <div className="tv-selos">
                  {giro.selos.map((s) => (
                    <span key={s.texto} className={`tv-selo tv-selo-${s.tom}`}>
                      {s.texto}
                    </span>
                  ))}
                </div>
              )}
              <div className="tv-detalhe">{giro?.subtitulo ?? k.giro.detalhe}</div>
            </>
          ) : (
            <Fora i={k.giro} />
          )}
        </section>
      </div>

      <div className="tv-linha2">
        <section className="tv-bloco tv-ranking">
          <h2 className="tv-rotulo">RANKING DA SEMANA</h2>
          {ranking.length === 0 ? (
            <div className="tv-ranking-vazio">
              <div className="tv-fora">sem ranking ainda</div>
              <div className="tv-motivo">Ninguém com item lançado nesta semana.</div>
            </div>
          ) : (
            <ol className="tv-ranking-lista">
              {ranking.map((l) => (
                <li key={l.accountId} className={`tv-pos-${String(l.posicao)}`}>
                  <span className="tv-pos">{l.posicao}º</span>
                  <span className="tv-pessoa">{l.nome}</span>
                  <strong>{inteiro(l.itens)}</strong>
                  <span className="tv-unid">itens</span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <div className="tv-coluna">
          <section className="tv-bloco tv-premio">
            <h2 className="tv-rotulo">PREMIAÇÃO DA SEMANA</h2>
            {k.premiacao.disponivel ? (
              <>
                <div className="tv-valor tv-valor-ouro">{reais(k.premiacao.valor, 0)}</div>
                {k.premiacao.detalhe && <div className="tv-detalhe">{k.premiacao.detalhe}</div>}
              </>
            ) : (
              <Fora i={k.premiacao} />
            )}
          </section>
          {alerta && (
            <section
              className={`tv-alerta tv-alerta-${alerta.severidade}`}
              aria-label="Precisa de atenção"
            >
              <Icone nome="warn" tamanho={30} traco={1.8} />
              <div>
                <strong>{alerta.titulo}</strong>
                {alerta.detalhe && <span>{alerta.detalhe}</span>}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function Variacao({ i }: { i: Disponivel }) {
  if (!i.variacao) return null;
  const tom = tomDaVariacao(i);
  return (
    <span className={`tv-var tv-var-${tom}`}>
      {i.variacao.pct !== 0 && (
        <span className={i.variacao.pct > 0 ? 'tv-seta' : 'tv-seta tv-seta-desce'}>
          <Icone nome="up-t" tamanho={20} traco={2.2} />
        </span>
      )}
      {pctTexto(i.variacao.pct)}
    </span>
  );
}
